import type { FichaCliente as Ficha } from '@/types/clientes';
import { nombreDeCliente } from '@/lib/llamadas/presentacion';
import { Icono } from '../Layout/iconos';

// Tarjeta del cliente vinculado a la llamada (prototipo iter5). Sin
// id_cliente: "cliente no identificado" con el teléfono de origen.

function Chip({ texto, tono }: { texto: string; tono: 'ok' | 'alerta' | 'neutro' }) {
  const estilos = {
    ok: { color: 'var(--status-ok-ink)', background: 'var(--status-ok-bg)' },
    alerta: { color: 'var(--status-esc-ink)', background: 'var(--status-esc-bg)' },
    neutro: { color: 'var(--text-2)', background: 'var(--surface-3)' },
  }[tono];
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
      style={estilos}
    >
      {texto}
    </span>
  );
}

export function FichaCliente({
  ficha,
  cargando,
  telefonoOrigen,
}: {
  ficha: Ficha | null;
  cargando: boolean;
  telefonoOrigen: string | null;
}) {
  return (
    <div
      data-testid="ficha-cliente"
      className="rounded-[var(--radius-sm)] border border-[var(--border-soft)] bg-[var(--surface-2)] p-3.5"
    >
      {cargando && <div className="text-[12.5px] text-[var(--text-dim)]">Cargando ficha…</div>}

      {!cargando && !ficha && (
        <div className="flex items-center gap-2.5 text-[12.5px] text-[var(--text-dim)]">
          <Icono nombre="users" className="h-4 w-4 shrink-0 text-[var(--text-mute)]" />
          <span>
            Cliente no identificado ·{' '}
            {/* Veracidad y no `??`, como la cabecera de esta misma pantalla: con el
                número retenido llega `anonymous` y `_norm_tel` lo guarda como cadena
                VACÍA, así que aquí se leía «Cliente no identificado · » y se cortaba,
                mientras dos líneas más arriba ponía «Número oculto». */}
            <span className="mono text-[var(--text-2)]">{telefonoOrigen || 'número oculto'}</span>
          </span>
        </div>
      )}

      {!cargando && ficha && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13.5px] font-semibold text-[var(--navy)]">
              {nombreDeCliente(ficha.razon_social, ficha.id_odoo)}
            </span>
            {ficha.cif && (
              <span className="mono text-[12px] text-[var(--text-dim)]">{ficha.cif}</span>
            )}
            {ficha.poblacion && (
              <span className="text-[12px] text-[var(--text-dim)]">· {ficha.poblacion}</span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {ficha.bloqueado && <Chip texto="Bloqueado · incidencias ERP" tono="alerta" />}
            {ficha.fuera_de_radio && <Chip texto="Fuera de radio" tono="alerta" />}
            {ficha.tiene_contrato ? (
              <Chip
                texto={`Contrato${ficha.tipo_contrato ? ` · ${ficha.tipo_contrato}` : ''}`}
                tono="ok"
              />
            ) : (
              <Chip texto="Sin contrato" tono="neutro" />
            )}
            {!ficha.activo && <Chip texto="Archivado en Odoo" tono="neutro" />}
          </div>

          {ficha.telefonos.length > 0 && (
            <div className="mono mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[var(--text-2)]">
              {ficha.telefonos.map((t) => (
                <span key={t.telefono}>
                  {t.telefono}
                  {t.origen === 'aprendido_en_llamada' && (
                    <span className="ml-1 font-sans text-[10.5px] text-[var(--text-mute)]">
                      (aprendido)
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}

          {ficha.equipos.length > 0 && (
            <ul className="mt-2.5 space-y-1 border-t border-[var(--border-soft)] pt-2.5">
              {ficha.equipos.map((e, i) => (
                <li key={i} className="flex items-center gap-2 text-[12px] text-[var(--text-2)]">
                  <Icono nombre="wrench" className="h-3.5 w-3.5 shrink-0 text-[var(--text-mute)]" />
                  <span className="mono">{e.referencia ?? 'sin ref.'}</span>
                  {e.tipo && <span>· {e.tipo}</span>}
                  {e.ubicacion && <span className="text-[var(--text-dim)]">· {e.ubicacion}</span>}
                  {e.tiene_ficha && <Chip texto="Con ficha" tono="ok" />}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
